import { DispatchStop } from './dispatch-planner.component';

export function formatDispatchTermin(termin?: string): string {
  const raw = (termin || '').trim();
  if (!raw) {
    return '';
  }

  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (isoMatch) {
    return `${isoMatch[3]}.${isoMatch[2]}.${isoMatch[1]}`;
  }

  const deMatch = /^(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec(raw);
  if (deMatch) {
    return `${deMatch[1].padStart(2, '0')}.${deMatch[2].padStart(2, '0')}.${deMatch[3]}`;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  return raw;
}

export function parsePlzOrtFromAddress(address?: string): { plz: string; ort: string } {
  const trimmed = (address || '').trim();
  if (!trimmed) {
    return { plz: '', ort: '' };
  }

  const match = /(?:^|[,\s])(\d{5})\s+([A-Za-zäöüÄÖÜß][A-Za-zäöüÄÖÜß0-9\s.\-()]+)/i.exec(trimmed);
  if (!match) {
    return { plz: '', ort: '' };
  }

  return {
    plz: match[1],
    ort: match[2].trim().replace(/\s+/g, ' ')
  };
}

export function getStopPlz(stop: DispatchStop): string {
  return (stop.plz || '').trim() || parsePlzOrtFromAddress(stop.address).plz;
}

export function getStopOrt(stop: DispatchStop): string {
  return (stop.ort || '').trim() || parsePlzOrtFromAddress(stop.address).ort;
}

export interface ParsedCustomerName {
  vorname: string;
  nachname: string;
  fullName: string;
}

/** Name aus mehrzeiliger Adresse (erste Zeile) oder „Nachname, Vorname“. */
export function parseCustomerNameFromAddress(address?: string): ParsedCustomerName {
  const trimmed = (address || '').trim();
  if (!trimmed) {
    return { vorname: '', nachname: '', fullName: '' };
  }

  const lines = trimmed.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
  const candidate = lines.length > 1 ? lines[0] : '';
  if (candidate && !looksLikeStreetOrPlzLine(candidate)) {
    return splitCustomerName(candidate);
  }

  return { vorname: '', nachname: '', fullName: '' };
}

export function splitCustomerName(full: string): ParsedCustomerName {
  const name = full.trim();
  if (!name) {
    return { vorname: '', nachname: '', fullName: '' };
  }

  const commaMatch = /^([^,]+),\s*(.+)$/.exec(name);
  if (commaMatch) {
    const nachname = commaMatch[1].trim();
    const vorname = commaMatch[2].trim();
    return {
      vorname,
      nachname,
      fullName: `${vorname} ${nachname}`.trim()
    };
  }

  const parts = name.split(/\s+/);
  if (parts.length > 1) {
    return {
      vorname: parts.slice(0, -1).join(' '),
      nachname: parts[parts.length - 1],
      fullName: name
    };
  }

  return { vorname: '', nachname: parts[0], fullName: name };
}

function looksLikeStreetOrPlzLine(line: string): boolean {
  if (/^\d{5}\s/.test(line)) {
    return true;
  }
  if (/^\d{5},/.test(line)) {
    return true;
  }
  if (/,\s*\d{5}\b/.test(line)) {
    return true;
  }
  if (/^\d{5}\s+[^,]+,\s*.+/.test(line)) {
    return true;
  }
  return false;
}
